import React, { useState, useEffect, useMemo } from 'react';
import { paymentsApi, clientsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Coins, Trash2, Download, Printer, Search } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PaymentsPage — Storico Movimenti / Acconti
 * Lista globale di tutti i pagamenti registrati con filtri per data, cliente, modalità.
 */
const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterClient, setFilterClient] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      paymentsApi.getAll(),
      clientsApi.getAll(),
    ]).then(([p, c]) => {
      setPayments(p.data);
      setClients(c.data);
    }).catch(() => toast.error('Errore caricamento movimenti'))
      .finally(() => setLoading(false));
  }, []);

  const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const formatDate = (s) => s ? new Date(s).toLocaleDateString('it-IT') : '-';

  const filtered = useMemo(() => {
    return payments.filter(p => {
      if (filterClient !== 'all' && p.client_id !== filterClient) return false;
      if (filterMethod !== 'all' && p.method !== filterMethod) return false;
      if (filterFrom && p.payment_date < filterFrom) return false;
      if (filterTo && p.payment_date > filterTo) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = ((p.client_name || '') + ' ' + (p.note || '') + ' ' + (p.method || '')).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [payments, filterClient, filterMethod, filterFrom, filterTo, search]);

  const totalFiltered = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo pagamento? L\'acconto verrà sottratto anche dal DDT collegato.')) return;
    try {
      await paymentsApi.delete(id);
      toast.success('Pagamento eliminato');
      setPayments(payments.filter(p => p.id !== id));
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadCsv = () => {
    const rows = [['Data', 'Cliente', 'DDT', 'Modalità', 'Note', 'Importo']];
    filtered.forEach(p => {
      rows.push([formatDate(p.payment_date), p.client_name || '', p.ddt_number || '', p.method, p.note || '', p.amount]);
    });
    rows.push(['', '', '', '', 'TOTALE', totalFiltered.toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `movimenti_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="payments-page" className="space-y-4">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Movimenti</h1>
          <p className="text-muted-foreground text-sm">Storico completo degli acconti e pagamenti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint}><Printer className="w-4 h-4 mr-2" />Stampa</Button>
          <Button variant="outline" size="sm" onClick={handleDownloadCsv}><Download className="w-4 h-4 mr-2" />Scarica CSV</Button>
        </div>
      </div>

      {/* Filtri */}
      <Card className="card-swiss no-print">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cerca</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="payments-search"
                  placeholder="Cliente, note, modalità..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cliente</Label>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger data-testid="filter-client" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Modalità</Label>
              <Select value={filterMethod} onValueChange={setFilterMethod}>
                <SelectTrigger data-testid="filter-method" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="Contanti">Contanti</SelectItem>
                  <SelectItem value="Bonifico">Bonifico</SelectItem>
                  <SelectItem value="Assegno">Assegno</SelectItem>
                  <SelectItem value="Carta">Carta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Dal</Label>
                <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Al</Label>
                <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="h-9" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Caricamento...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Coins className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun movimento registrato {payments.length > 0 ? '(filtri attivi)' : ''}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss">Data</TableHead>
                  <TableHead className="table-header-swiss">Cliente</TableHead>
                  <TableHead className="table-header-swiss">DDT</TableHead>
                  <TableHead className="table-header-swiss">Modalità</TableHead>
                  <TableHead className="table-header-swiss">Note</TableHead>
                  <TableHead className="table-header-swiss text-right">Importo</TableHead>
                  <TableHead className="table-header-swiss w-[60px] no-print"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono']">{formatDate(p.payment_date)}</TableCell>
                    <TableCell className="table-cell-swiss font-medium">{p.client_name || '—'}</TableCell>
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] text-xs">
                      {p.ddt_id ? (
                        <span className="text-foreground">{p.ddt_number ? `${p.ddt_number}-${p.ddt_year}` : 'DDT'}</span>
                      ) : (
                        <span className="text-muted-foreground italic">Generico</span>
                      )}
                    </TableCell>
                    <TableCell className="table-cell-swiss"><Badge variant="outline">{p.method}</Badge></TableCell>
                    <TableCell className="table-cell-swiss text-muted-foreground text-sm">{p.note || '-'}</TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono'] font-bold text-green-700">{formatCurrency(p.amount)}</TableCell>
                    <TableCell className="table-cell-swiss no-print">
                      <Button data-testid={`delete-payment-${p.id}`} variant="ghost" size="icon" onClick={() => handleDelete(p.id)} className="h-7 w-7 text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <tfoot>
                <TableRow className="bg-muted/40">
                  <TableCell colSpan={5} className="text-right font-bold">
                    TOTALE ({filtered.length} {filtered.length === 1 ? 'movimento' : 'movimenti'})
                  </TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] font-bold text-green-700">
                    {formatCurrency(totalFiltered)}
                  </TableCell>
                  <TableCell className="no-print"></TableCell>
                </TableRow>
              </tfoot>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
